import React, { useState } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Search,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import { SUPPORTED_METRICS, MetricDefinition } from '../config/sensorMetrics';

interface SensorMetricsReferenceProps {
  protocol?: 'http' | 'mqtt' | 'both';
}

const SensorMetricsReference: React.FC<SensorMetricsReferenceProps> = ({ protocol = 'both' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopyField = (fieldName: string) => {
    navigator.clipboard.writeText(fieldName);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const filterMetrics = (metrics: MetricDefinition[]) => {
    if (!searchTerm) return metrics;
    const term = searchTerm.toLowerCase();
    return metrics.filter(m =>
      m.userFriendly.toLowerCase().includes(term) ||
      m.internal.toLowerCase().includes(term) ||
      m.description.toLowerCase().includes(term) ||
      m.unit.toLowerCase().includes(term)
    );
  };

  const getProtocolText = () => {
    if (protocol === 'http') return 'HTTP API';
    if (protocol === 'mqtt') return 'MQTT';
    return 'HTTP & MQTT';
  };

  const filteredMetrics = filterMetrics(SUPPORTED_METRICS);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📊 Supported Sensor Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Send any combination of these measurements via {getProtocolText()}.
          Use the <strong>Field Name</strong> in your payload. Click the copy icon to copy field names.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search metrics (e.g., temperature, wind, humidity)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      {filteredMetrics.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Field Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>Unit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#1976d2' }}>Example</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>Copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMetrics.map((metric, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    '&:hover': {
                      bgcolor: '#f9f9f9',
                      cursor: 'pointer'
                    },
                    bgcolor: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                  }}
                  onClick={() => handleCopyField(metric.userFriendly)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <code style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontWeight: 600
                      }}>
                        {metric.userFriendly}
                      </code>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                      → {metric.internal}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {metric.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={metric.unit}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <code style={{
                      fontSize: '13px',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      color: '#2e7d32',
                      fontWeight: 600
                    }}>
                      {metric.example}
                    </code>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={copiedField === metric.userFriendly ? 'Copied!' : 'Click to copy'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyField(metric.userFriendly);
                        }}
                      >
                        {copiedField === metric.userFriendly ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <ContentCopy fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No metrics found matching "{searchTerm}"
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: '#2e7d32' }}>
          <strong>💡 Tip:</strong> You don't need to send all fields! Only include the measurements your sensor can provide.
          Missing or null values are handled automatically.
        </Typography>
      </Box>
    </Box>
  );
};

export default SensorMetricsReference;
